import { useState } from 'react';
import { store } from '@/ui/store';
import { useStore } from '@/ui/useStore';
import type { Border, CellStyle } from '@/model/types';
import type { BorderTarget } from '@/ui/store';
import { FORMATS } from '@/engine/format';
import { Popover } from '@/ui/Popover';
import { Icon } from '@/ui/icons';
import { SearchSelect, type SearchOption } from '@/ui/SearchSelect';
import { prettyField } from '@/data/classify';
import { anyFilter, isFiltered, parseCondition } from '@/model/filters';
import { bindingLabel, chipColour, parseOptions } from '@/model/dropdowns';
import { SOURCE_NAMES } from '@/engine/context';
import type { FieldBinding } from '@/model/types';
import { colToLabel } from '@/model/address';

/**
 * The right-click menu: a LIST of items, not a panel of controls.
 *
 * It was a grid of small buttons and swatches, which put "delete 5 rows" and
 * "make this bold" in the same visual register and made the actions read as
 * loose text. A menu is a column of things you can do — icon, label, one per
 * line, ruled into groups — and anything that needs a picker opens it inline
 * underneath rather than as a submenu that has to be positioned and chased.
 */

/**
 * Swatches, not a full picker, for the common case — with a native colour input
 * beside them for anything else. The greys and the accent are the palette the
 * app already uses; the rest are the validated categorical hues, so a sheet
 * coloured by hand still reads as one system with its charts.
 */
const TEXT_SWATCHES = ['#1a1c1f', '#63676e', '#b83e1b', '#3370bd', '#0f9d8a', '#9a7a1b', '#a86fc2'];
const FILL_SWATCHES = ['#ffffff', '#f1f2f4', '#d2ebf7', '#e3f5d6', '#fef9e3', '#fff1ea', '#efe3fb'];
const LINE_SWATCHES = ['#1a1c1f', '#63676e', '#c4c7cc', '#3370bd', '#b83e1b'];

const THICKNESSES: { w: number; label: string }[] = [
  { w: 0.5, label: 'Hairline' },
  { w: 1, label: 'Medium' },
  { w: 1.5, label: 'Thick' },
];

/**
 * An edge here means the edge of the SELECTION, so Top on a three-row range
 * draws one line above it — not a line above every row. `Inside` is the other
 * half: the gridlines between cells, leaving the outside alone.
 */
const EDGES: { id: BorderTarget; label: string; title: string }[] = [
  { id: 'outline', label: 'Box', title: 'Outline the selection' },
  { id: 'inside', label: 'Inside', title: 'Lines between the cells' },
  { id: 'all', label: 'All', title: 'Every edge of every cell' },
  { id: 'top', label: 'Top', title: 'Top edge of the selection' },
  { id: 'bottom', label: 'Bottom', title: 'Bottom edge of the selection' },
  { id: 'left', label: 'Left', title: 'Left edge of the selection' },
  { id: 'right', label: 'Right', title: 'Right edge of the selection' },
  { id: 'none', label: 'None', title: 'Remove every border' },
];

type Expanded = 'text' | 'fill' | 'borders' | 'number' | 'overflow' | 'filter' | 'list' | null;

/**
 * The menu has two pages.
 *
 * Everything fitted on one until it did not: on a row of the Features sheet the
 * list ran cut, copy, paste, insert, insert, delete, clear, hide, freeze,
 * unfreeze, resize, bold, italic, long text, align, text colour, fill colour,
 * borders, number format, clear formatting — twenty rows, past the bottom of a
 * laptop screen, with the thing you right-clicked for somewhere in the middle.
 *
 * The split is by KIND of intention rather than by frequency: the first page
 * changes what the sheet holds and where it is, the second changes how it
 * looks. Styling is the half that is browsed — "what colours are there?" — and
 * browsing is what a page is for.
 */
type Page = 'edit' | 'style';

interface Props {
  x: number;
  y: number;
  onClose: () => void;
}

/**
 * The active cell's own border, as a single thickness+colour to seed the
 * picker with — or the default when it has none, or when its edges disagree
 * (picking one of several would claim a uniform border that is not there).
 */
function currentBorder(style: CellStyle): Border {
  const edges = style.borders ? Object.values(style.borders) : [];
  const first = edges[0];
  if (first && edges.every((b) => b.w === first.w && b.c === first.c)) return first;
  return { w: 0.5, c: '#1a1c1f' };
}

export function FormatMenu({ x, y, onClose }: Props): JSX.Element {
  useStore();
  const active = store.activeStyle();
  // Seeded from the active cell's OWN border, not a hardcoded default — this
  // menu remounts fresh every time it opens (Grid.tsx), so a plain default
  // here meant the thickness/colour picker always showed "Hairline, black"
  // as selected even when the cell already had a 3px border, making a
  // thickness CHANGE look like it silently did nothing.
  const [line, setLine] = useState<Border>(() => currentBorder(active));
  const [open, setOpen] = useState<Expanded>(null);
  const [page, setPage] = useState<Page>('edit');
  /** Delete is armed: the second, confirming click has not happened yet. */
  const [armed, setArmed] = useState(false);
  /** Any other click in the menu cancels an armed delete. */
  const disarm = (): void => setArmed(false);

  const band = store.selectedBand();
  const db = store.databaseInSelection();
  const noun = band?.axis === 'row' ? 'row' : 'column';
  const n = band?.count ?? 1;
  const plural = n === 1 ? noun : `${n} ${noun}s`;
  /**
   * How many ROWS the current selection touches, regardless of whether it is
   * a whole-row band or just a cell range — `selectFeaturesFromRows`/
   * `zoomToFeaturesFromRows` already walk `sel.r1..r2` directly, not `band`,
   * so the menu item they are behind should not need a row band selected
   * either: right-clicking a plain cell is enough.
   */
  const mapRowCount = store.sel.r2 - store.sel.r1 + 1;
  /*
   * On the Usages sheet the sheet IS the project: a row is a usage and a column
   * is a field on every usage. Deleting either reaches the project, so the menu
   * says what it will actually do and arms before doing it.
   */
  const usages = band ? store.usagesToDelete(band.axis, band.from, band.to) : null;
  const fields = band ? store.usageFieldsToDelete(band.axis, band.from, band.to) : null;
  /*
   * On the Blocks sheet a cell edit applies itself, but a ROW or COLUMN change
   * does not: a deleted column takes that property off every block in the
   * project. Typing does not ask; this does.
   */
  const blockWarning = band ? store.blockStructuralWarning(band.axis, band.from, band.to) : null;
  const project = usages ?? fields;
  const projectNoun = usages
    ? usages.length === 1
      ? `"${usages[0]}"`
      : `${usages.length} usages`
    : fields
      ? fields.length === 1
        ? `"${prettyField(fields[0]!)}" from every usage`
        : `${fields.length} fields from every usage`
      : '';
  const mod = navigator.platform.toLowerCase().includes('mac') ? '⌘' : 'Ctrl+';
  const spread = store.sel.r2 > store.sel.r1 || store.sel.c2 > store.sel.c1;
  const mergeHere = store.mergeInSelection();

  const run = (fn: () => void) => () => {
    fn();
    onClose();
  };

  return (
    // Anchored to the pointer, and closed by a scroll: a menu still pinned over
    // the sheet after it has scrolled is pointing at the wrong cell.
    <Popover anchor={{ x, y }} gap={0} onClose={onClose} closeOnScroll swallowOutsidePress className="gt-menu-panel">
      {page === 'edit' ? (
        <>
      {store.isReadOnlySheet() ? null : (
        <Item
          onOther={disarm}
          icon="cut"
          label="Cut"
          shortcut={`${mod}X`}
          onClick={run(() => void store.clipboardCopy(true).then(warnIfRefused))}
        />
      )}
      <Item
        onOther={disarm}
        icon="copy"
        label="Copy"
        shortcut={`${mod}C`}
        onClick={run(() => void store.clipboardCopy(false).then(warnIfRefused))}
      />
      {store.isReadOnlySheet() ? null : (
        <Item
          onOther={disarm}
          icon="paste"
          label="Paste"
          shortcut={`${mod}V`}
          title="Needs clipboard permission. The keyboard shortcut always works."
          onClick={run(() => void store.clipboardPaste().then(warnIfRefused))}
        />
      )}

      {/* The row holds a database: what is done to IT, before what is done
          to the row. Deleting the row would take the database with it, but
          nobody reads "Delete row" as "delete the database". */}
      {db ? (
        <>
          <Divider />
          <Item
            onOther={disarm}
            icon="database"
            label="Edit database…"
            title="Criteria and columns"
            onClick={run(() => store.openDbWizard(db.id))}
          />
          <Item
            onOther={disarm}
            icon={db.collapsed ? 'show' : 'hide'}
            label={db.collapsed ? 'Show sub-rows' : 'Hide sub-rows'}
            onClick={run(() => store.updateDatabase(db.id, { collapsed: !db.collapsed }, db.collapsed ? 'Show sub-rows' : 'Hide sub-rows'))}
          />
          <Item
            onOther={disarm}
            icon="select"
            label="Select all on the map"
            onClick={run(() => void store.selectDbObjects(db.id, null))}
          />
          <Item
            onOther={disarm}
            icon="trash"
            danger
            label="Delete database"
            title="Clears the database row and its sub-rows. The titles above are ordinary cells and stay. Cmd/Ctrl+Z brings it back."
            onClick={run(() => store.deleteDatabase(db.id))}
          />
        </>
      ) : null}

      {band ? (
        <>
          <Divider />
          {store.isReadOnlySheet() ? null : (
            <>
              <Item
                onOther={disarm}
                icon="plus"
                label={`Insert ${n} ${noun}${n === 1 ? '' : 's'} ${band.axis === 'row' ? 'above' : 'left'}`}
                onClick={run(() => store.insertBand(band.axis, band.from, n))}
              />
              <Item
                onOther={disarm}
                icon="plus"
                label={`Insert ${n} ${noun}${n === 1 ? '' : 's'} ${band.axis === 'row' ? 'below' : 'right'}`}
                onClick={run(() => store.insertBand(band.axis, band.to + 1, n))}
              />
              <Item
                icon="trash"
                danger
                label={project ? `Delete ${projectNoun}` : `Delete ${plural}`}
                title={
                  blockWarning
                    ? blockWarning
                    : usages
                    ? `Remove ${usages.join(', ')} from the project. This cannot be undone from here.`
                    : fields
                      ? `Remove ${fields.map(prettyField).join(', ')} from every usage in the project. This cannot be undone from here.`
                      : `Delete ${plural} and pull everything after them back. Formulas that pointed at them become #REF!.`
                }
                onClick={() => {
                  // Deleting on an ordinary sheet is undoable, so it just happens.
                  // Deleting a USAGE or a FIELD reaches the project and cannot be
                  // taken back here, so it arms first.
                  if (!project && !blockWarning) {
                    store.deleteBand(band.axis, band.from, band.to);
                    onClose();
                    return;
                  }
                  setArmed(true);
                }}
              >
                {armed && !project && blockWarning ? (
                  <button
                    className="gt-confirm"
                    autoFocus
                    title={blockWarning}
                    onClick={(e) => {
                      e.stopPropagation();
                      store.deleteBand(band.axis, band.from, band.to);
                      store.autoPushBlocks();
                      onClose();
                    }}
                  >
                    Sure?
                  </button>
                ) : null}
                {armed && project ? (
                  // Right here in the row, so confirming is a few pixels of travel
                  // rather than a dialog in the middle of the screen.
                  <button
                    className="gt-confirm"
                    autoFocus
                    title={
                      usages
                        ? `Delete ${usages.join(', ')} from the project`
                        : `Remove ${fields!.map(prettyField).join(', ')} from every usage`
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      // The result is not discarded: it reaches the toolbar, where
                      // a refusal is visible instead of looking like nothing
                      // happened.
                      if (usages) void store.deleteUsages(usages);
                      else void store.deleteUsageFields(fields!);
                      onClose();
                    }}
                  >
                    Sure?
                  </button>
                ) : null}
              </Item>
            </>
          )}

          {store.isReadOnlySheet() ? null : (
            <Item onOther={disarm} icon="eraser" label={`Clear ${plural}`} onClick={run(() => store.clearSelection())} />
          )}
          <Item
            onOther={disarm}
            icon="hide"
            label={`Hide ${plural}`}
            title="The rows keep existing — formulas pointing at them still work. They just have no height."
            onClick={run(() => store.hideBand(band.axis, band.from, band.to))}
          />
          {store.hasHiddenNear(band.axis, band.from, band.to) ? (
            <Item
              onOther={disarm}
              icon="show"
              label={`Unhide ${noun}s`}
              onClick={run(() => store.showBand(band.axis, band.from, band.to))}
            />
          ) : null}
          {band.axis === 'col' ? (
            <>
              <Item
                onOther={disarm}
                icon="sortAsc"
                label={`Sort sheet A → Z`}
                title={`Sort every row by column ${colToLabel(band.from)}. Row 1 stays as the header, and formulas follow their cells.`}
                onClick={run(() => store.sortByColumn(band.from, false))}
              />
              <Item
                onOther={disarm}
                icon="sortDesc"
                label={`Sort sheet Z → A`}
                title={`Sort every row by column ${colToLabel(band.from)}, largest first.`}
                onClick={run(() => store.sortByColumn(band.from, true))}
              />
              <Item
                onOther={disarm}
                icon="filter"
                label={isFiltered(store.sheet(), band.from) ? `Filter on ${colToLabel(band.from)}…` : `Filter ${colToLabel(band.from)}…`}
                title="Show only some rows: tick the values you want, or write a condition."
                expandable
                expanded={open === 'filter'}
                onClick={() => setOpen(open === 'filter' ? null : 'filter')}
              />
              {open === 'filter' ? <FilterDrawer col={band.from} /> : null}
              {store.isReadOnlySheet() ? null : (
                <>
                  <Item
                    onOther={disarm}
                    icon="chip"
                    label={store.columnList(band.from) ? `Edit dropdown on ${colToLabel(band.from)}…` : `Dropdown on ${colToLabel(band.from)}…`}
                    title="Offer a fixed list of values in this column, drawn as chips. It can be a list you type or a field from the project."
                    expandable
                    expanded={open === 'list'}
                    onClick={() => setOpen(open === 'list' ? null : 'list')}
                  />
                  {open === 'list' ? <ListDrawer axis="col" index={band.from} /> : null}
                </>
              )}
              {anyFilter(store.sheet()) ? (
                <Item
                  onOther={disarm}
                  icon="show"
                  label="Clear all filters"
                  onClick={run(() => store.clearFilters())}
                />
              ) : null}
              <Divider />
            </>
          ) : null}
          {band.axis === 'row' && !store.isReadOnlySheet() ? (
            <>
              {/* The same control the other way round, for a sheet laid out
                  across rather than down. */}
              <Item
                onOther={disarm}
                icon="chip"
                label={store.rowList(band.from) ? `Edit dropdown on row ${band.from}…` : `Dropdown on row ${band.from}…`}
                title="Offer a fixed list of values along this row, drawn as chips. A column's list wins where the two cross."
                expandable
                expanded={open === 'list'}
                onClick={() => setOpen(open === 'list' ? null : 'list')}
              />
              {open === 'list' ? <ListDrawer axis="row" index={band.from} /> : null}
              <Divider />
            </>
          ) : null}
          <Item
            onOther={disarm}
            icon="freeze"
            label={`Freeze up to ${noun} ${band.to}`}
            title={`Keep ${band.axis === 'row' ? `rows 1–${band.to}` : `columns 1–${band.to}`} on screen however far the sheet is scrolled.`}
            onClick={run(() => store.freezeUpTo(band.axis, band.to))}
          />
          {(band.axis === 'row' ? store.sheet().freezeRows : store.sheet().freezeCols) ? (
            <Item
              onOther={disarm}
              icon="show"
              label={`Unfreeze ${noun}s`}
              onClick={run(() => store.unfreeze(band.axis))}
            />
          ) : null}
          <Item
            onOther={disarm}
            icon="resize"
            label={`Resize ${plural}…`}
            onClick={run(() => {
              const current = band.axis === 'row' ? 24 : 96;
              const answer = prompt(
                `${band.axis === 'row' ? 'Row height' : 'Column width'} in pixels`,
                String(current)
              );
              const size = Number(answer);
              if (answer !== null && Number.isFinite(size) && size > 0) {
                store.resizeBand(band.axis, band.from, band.to, size);
              }
            })}
          />
        </>
      ) : null}
      {store.sheet().role === 'features' || store.sheet().role === 'areas' ? (
        <>
          <Divider />
          {/* Every row the selection touches names a thing on the map — a row
              band selected by its header, or just a plain cell range. */}
          <Item
            onOther={disarm}
            icon="zoom"
            label={mapRowCount === 1 ? 'Zoom to on map' : `Zoom to ${mapRowCount} on map`}
            title="Move the map to fit what these rows name."
            onClick={run(() => void store.zoomToFeaturesFromRows())}
          />
          <Item
            onOther={disarm}
            icon="select"
            label={mapRowCount === 1 ? 'Select on map' : `Select ${mapRowCount} on map`}
            title="Select them on the map, as if they had been clicked there."
            onClick={run(() => void store.selectFeaturesFromRows())}
          />
        </>
      ) : null}


      {store.isReadOnlySheet() ? null : (
        <>
          <Divider />
          <Item
            onOther={disarm}
            icon="brush"
            label="Style"
            title="Bold, colours, borders, number format."
            expandable
            onClick={() => {
              setOpen(null);
              setPage('style');
            }}
          />
          <Item onOther={disarm} icon="eraser" label="Clear contents" shortcut="Delete" onClick={run(() => store.clearSelection())} />
          {spread && !mergeHere ? (
            <Item
              onOther={disarm}
              icon="merge"
              label="Merge cells"
              onClick={run(() => {
                const { r1, c1, r2, c2 } = store.sel;
                if (
                  store.mergeWouldDiscard(r1, c1, r2, c2) &&
                  !confirm("Merging keeps only the top-left cell's content — everything else in the selection will be cleared. Continue?")
                ) {
                  return;
                }
                store.mergeCells(r1, c1, r2, c2);
              })}
            />
          ) : null}
          {mergeHere ? (
            <Item onOther={disarm} icon="unmerge" label="Unmerge cells" onClick={run(() => store.unmergeCells(mergeHere))} />
          ) : null}
        </>
      )}
        </>
      ) : (
        <>
      <div className="gt-menu-back" role="menuitem" tabIndex={0} onClick={() => setPage('edit')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setPage('edit'); }}>
        <span className="gt-menu-chevron is-back"><Icon.chevron /></span>
        <span className="gt-menu-label">Style</span>
      </div>
      <Item
        onOther={disarm}
        icon="bold"
        label="Bold"
        shortcut={`${mod}B`}
        checked={store.selectionHas('bold')}
        onClick={() => store.applyStyle({ bold: !store.selectionHas('bold') })}
      />
      <Item
        onOther={disarm}
        icon="italic"
        label="Italic"
        checked={store.selectionHas('italic')}
        onClick={() => store.applyStyle({ italic: !store.selectionHas('italic') })}
      />
      <Item
        onOther={disarm}
        icon="resize"
        label="Long text"
        expandable
        expanded={open === 'overflow'}
        onClick={() => setOpen(open === 'overflow' ? null : 'overflow')}
      />
      {open === 'overflow' ? (
        <Drawer>
          <div className="gt-menu-inline">
            <button className="gt-chip-btn" title="Spill into the empty cells beside it" onClick={() => store.setOverflow('overrun')}>Overrun</button>
            <button className="gt-chip-btn" title="Cut off at the cell edge" onClick={() => store.setOverflow('clip')}>Clip</button>
            <button className="gt-chip-btn" title="Wrap onto more lines and grow the row" onClick={() => store.setOverflow('wrap')}>Wrap</button>
          </div>
        </Drawer>
      ) : null}

      <Item onOther={disarm} icon="text" label="Align">
        <div className="gt-menu-inline">
          <button className="gt-chip-btn" onClick={() => store.applyStyle({ align: 'left' })} title="Left"><Icon.alignLeft /></button>
          <button className="gt-chip-btn" onClick={() => store.applyStyle({ align: 'center' })} title="Centre"><Icon.alignCenter /></button>
          <button className="gt-chip-btn" onClick={() => store.applyStyle({ align: 'right' })} title="Right"><Icon.alignRight /></button>
        </div>
      </Item>

      <Divider />
      <Item
        onOther={disarm}
        icon="text"
        label="Text colour"
        expandable
        expanded={open === 'text'}
        onClick={() => setOpen(open === 'text' ? null : 'text')}
      />
      {open === 'text' ? (
        <Drawer>
          <Swatches
            colors={TEXT_SWATCHES}
            current={active.color}
            onPick={(color) => store.applyStyle({ color })}
            onClear={() => store.applyStyle({ color: undefined })}
            clearLabel="Default"
          />
        </Drawer>
      ) : null}

      <Item
        onOther={disarm}
        icon="fill"
        label="Fill colour"
        expandable
        expanded={open === 'fill'}
        onClick={() => setOpen(open === 'fill' ? null : 'fill')}
      />
      {open === 'fill' ? (
        <Drawer>
          <Swatches
            colors={FILL_SWATCHES}
            current={active.bg}
            onPick={(bg) => store.applyStyle({ bg })}
            onClear={() => store.applyStyle({ bg: undefined })}
            clearLabel="None"
          />
        </Drawer>
      ) : null}

      <Item
        onOther={disarm}
        icon="borders"
        label="Borders"
        expandable
        expanded={open === 'borders'}
        onClick={() => setOpen(open === 'borders' ? null : 'borders')}
      />
      {open === 'borders' ? (
        <Drawer>
          <div className="gt-menu-inline">
            {THICKNESSES.map((t) => (
              <button
                key={t.w}
                className={`gt-chip-btn${line.w === t.w ? ' is-on' : ''}`}
                title={t.label}
                onClick={() => setLine((l) => ({ ...l, w: t.w }))}
              >
                <span className="gt-line-sample" style={{ height: t.w, background: line.c }} />
              </button>
            ))}
            <Swatches colors={LINE_SWATCHES} current={line.c} onPick={(c) => setLine((l) => ({ ...l, c }))} />
          </div>
          <div className="gt-menu-inline gt-menu-wrap">
            {EDGES.map((edge) => (
              <button
                key={edge.id}
                className={`gt-chip-btn${edge.id === 'none' ? ' is-danger' : ''}`}
                title={edge.title}
                // The drawer stays open: borders are built up a side at a time,
                // and closing after each click makes Top-then-Left four gestures.
                onClick={() => store.applyBorders(edge.id, edge.id === 'none' ? null : line)}
              >
                {edge.label}
              </button>
            ))}
          </div>
        </Drawer>
      ) : null}

      <Item
        onOther={disarm}
        icon="number"
        label="Number format"
        expandable
        expanded={open === 'number'}
        onClick={() => setOpen(open === 'number' ? null : 'number')}
      />
      {open === 'number' ? (
        <Drawer>
          <select
            className="gt-select"
            value={store.activeFormat() ?? 'general'}
            onChange={(e) => store.applyFormat(e.target.value as (typeof FORMATS)[number]['id'])}
          >
            {FORMATS.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
        </Drawer>
      ) : null}
          <Divider />
          <Item onOther={disarm} icon="brush" label="Clear formatting" shortcut={`${mod}\\`} onClick={run(() => store.clearFormatting())} />
        </>
      )}
    </Popover>
  );
}

/**
 * The browser can refuse clipboard access, and a menu item that silently does
 * nothing is worse than one that says why. The keyboard shortcut goes through
 * the grid's own copy/paste events and is never refused.
 */
function warnIfRefused(ok: boolean): void {
  if (!ok) alert('The browser would not give access to the clipboard.\n\nUse the keyboard shortcut instead — that route always works.');
}

function Divider(): JSX.Element {
  return <div className="gt-menu-divider" />;
}

/** Anything that needs a picker opens it here, under its own item. */
function Drawer({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="gt-menu-drawer">{children}</div>;
}

interface ItemProps {
  icon: keyof typeof Icon;
  label: string;
  shortcut?: string;
  title?: string;
  danger?: boolean;
  checked?: boolean;
  expandable?: boolean;
  expanded?: boolean;
  onClick?: () => void;
  /** Clicking any OTHER item cancels an armed delete. */
  onOther?: () => void;
  children?: React.ReactNode;
}

function Item({
  icon,
  label,
  onOther,
  shortcut,
  title,
  danger,
  checked,
  expandable,
  expanded,
  onClick,
  children,
}: ItemProps): JSX.Element {
  const Glyph = Icon[icon];
  /*
   * A DIV, not a button.
   *
   * A menu row can contain controls of its own — the alignment chips, the
   * "Sure?" confirm — and a <button> inside a <button> is invalid HTML that
   * does not reliably receive its clicks: the outer button swallows them. That
   * is why confirming a delete appeared to do nothing.
   *
   * So the row carries the menu semantics itself and behaves like a button for
   * anyone not using a mouse: it takes focus, and Enter or Space activates it.
   */
  return (
    <div
      className={`gt-menu-row${danger ? ' is-danger' : ''}${checked ? ' is-checked' : ''}`}
      role="menuitem"
      tabIndex={0}
      title={title}
      onClick={() => {
        onOther?.();
        onClick?.();
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        // A key pressed on a control INSIDE the row is that control's business.
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        onOther?.();
        onClick?.();
      }}
    >
      <Glyph />
      <span className="gt-menu-label">{label}</span>
      {children}
      {shortcut ? <span className="gt-menu-shortcut">{shortcut}</span> : null}
      {expandable ? (
        <span className={`gt-menu-chevron${expanded ? ' is-open' : ''}`}>
          <Icon.chevron />
        </span>
      ) : null}
    </div>
  );
}

function Swatches({
  colors,
  current,
  onPick,
  onClear,
  clearLabel,
}: {
  colors: string[];
  current?: string;
  onPick: (color: string) => void;
  onClear?: () => void;
  clearLabel?: string;
}): JSX.Element {
  return (
    <div className="gt-swatches">
      {colors.map((color) => (
        <button
          key={color}
          className={`gt-swatch${current?.toLowerCase() === color.toLowerCase() ? ' is-on' : ''}`}
          style={{ background: color }}
          title={color}
          onClick={() => onPick(color)}
        />
      ))}
      <label className="gt-swatch gt-swatch-custom" title="Custom colour">
        <input type="color" value={current ?? '#1a1c1f'} onChange={(e) => onPick(e.target.value)} />
      </label>
      {onClear ? (
        <button className="gt-chip-btn" onClick={onClear}>
          {clearLabel ?? 'None'}
        </button>
      ) : null}
    </div>
  );
}

/**
 * A column filter: tick the values, or write a condition, or both.
 *
 * Two ways of saying the same kind of thing because they answer different
 * questions. A list answers "which of these", needs no syntax and shows what is
 * actually there; an expression answers "everything over 5000", which a list of
 * forty distinct prices cannot. They AND together.
 *
 * The list stores what is EXCLUDED, not what is included: a value that appears
 * after the filter was set is then visible by default, where an include-list
 * would silently swallow new rows.
 */
function FilterDrawer({ col }: { col: number }): JSX.Element {
  const current = store.columnFilter(col);
  const [expr, setExpr] = useState(current.expr ?? '');
  const [query, setQuery] = useState('');
  const values = store.columnValues(col);
  const hidden = new Set(current.hidden ?? []);
  const shown = query.trim()
    ? values.filter((v) => v.text.toLowerCase().includes(query.trim().toLowerCase()))
    : values;
  const error = parseCondition(expr).error;

  const setHidden = (next: Set<string>): void =>
    store.setColumnFilter(col, { ...store.columnFilter(col), hidden: [...next] });

  return (
    <Drawer>
      <input
        className="gt-input"
        placeholder="Condition — >5000, ~tower, >=100 and <500"
        value={expr}
        spellCheck={false}
        onChange={(e) => setExpr(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') store.setColumnFilter(col, { ...store.columnFilter(col), expr });
        }}
        onBlur={() => store.setColumnFilter(col, { ...store.columnFilter(col), expr })}
      />
      {error ? <div className="gt-filter-error">{error}</div> : null}
      <div className="gt-filter-hint">A bare word means “contains”. Row 1 stays as the header.</div>

      <input
        className="gt-input"
        placeholder="Search values"
        value={query}
        spellCheck={false}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
      />
      <div className="gt-menu-inline">
        <button
          className="gt-chip-btn"
          onClick={() => setHidden(new Set([...hidden].filter((h) => !shown.some((v) => v.text === h))))}
        >
          Select all
        </button>
        <button
          className="gt-chip-btn"
          onClick={() => setHidden(new Set([...hidden, ...shown.map((v) => v.text)]))}
        >
          Clear
        </button>
      </div>
      <div className="gt-filter-list">
        {shown.length === 0 ? <div className="gt-filter-hint">Nothing in this column.</div> : null}
        {shown.map((v) => (
          <label className="gt-filter-row" key={v.text}>
            <input
              type="checkbox"
              checked={!hidden.has(v.text)}
              onChange={(e) => {
                const next = new Set(hidden);
                if (e.target.checked) next.delete(v.text);
                else next.add(v.text);
                setHidden(next);
              }}
            />
            <span className={v.text === '' ? 'gt-filter-blank' : undefined}>{v.text === '' ? '(blank)' : v.text}</span>
            <span className="gt-filter-count">{v.count}</span>
          </label>
        ))}
      </div>
    </Drawer>
  );
}


/**
 * The values a column offers, one per line.
 *
 * Seeded from what the column already holds, because on real data that is the
 * list — a status column already contains every status anyone has used, and
 * asking someone to retype them is asking them to make a typo.
 *
 * The list is a suggestion, not a rule: a value that is not on it still goes in
 * and shows outlined. A table over live project data that refuses what the
 * project contains is worse than one that flags it, and this is the version of
 * data validation worth having.
 */
function ListDrawer({ axis, index }: { axis: 'row' | 'col'; index: number }): JSX.Element {
  const current = store.bandList(axis, index);
  const bound = current?.field;
  const [text, setText] = useState((bound ? [] : (current?.options ?? [])).join('\n'));
  const options = parseOptions(text);
  const where = axis === 'row' ? `row ${index}` : `column ${colToLabel(index)}`;

  const save = (next: string[]): void => {
    setText(next.join('\n'));
    store.setBandList(axis, index, next.length > 0 ? { options: next } : undefined);
  };
  const type = (next: string[]): void => {
    // Typing a list REPLACES a binding rather than sitting alongside it: two
    // sources for one column's values is a column whose values nobody can
    // predict.
    store.setBandList(axis, index, next.length > 0 ? { options: next } : undefined);
  };

  return (
    <Drawer>
      <FieldPicker
        value={bound}
        onPick={(field) => (field ? store.bindBandToField(axis, index, field) : save([]))}
      />
      {bound ? (
        <>
          <span className="gt-menu-note">
            {current!.options.length} value{current!.options.length === 1 ? '' : 's'} from{' '}
            <code>{bindingLabel(bound)}</code> — every cell in {where} offers them, and the list
            follows the project.
          </span>
          <div className="gt-menu-inline">
            <button
              className="gt-chip-btn"
              title="Keep these values as a plain list, no longer tied to the project"
              onClick={() => save(current!.options)}
            >
              Detach
            </button>
            <button className="gt-chip-btn" onClick={() => save([])}>
              Remove
            </button>
          </div>
        </>
      ) : (
        <>
          <textarea
            className="gt-input gt-list-input"
            rows={5}
            placeholder={'One value per line\nDone\nIn progress\nBlocked'}
            value={text}
            spellCheck={false}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            onBlur={() => type(options)}
          />
          <div className="gt-menu-inline">
            <button className="gt-chip-btn" onClick={() => type(options)}>
              Apply
            </button>
            {axis === 'col' ? (
              <button
                className="gt-chip-btn"
                title="Use the values already in this column"
                onClick={() => save(store.suggestList(index))}
              >
                From column
              </button>
            ) : null}
            {current ? (
              <button className="gt-chip-btn" onClick={() => save([])}>
                Remove
              </button>
            ) : null}
          </div>
        </>
      )}
      {(bound ? current!.options : options).length > 0 ? (
        <div className="gt-menu-inline gt-list-preview">
          {(bound ? current!.options : options).map((o, i) => (
            <span key={o} className="gt-chip-cell" style={{ background: chipColour(i) }}>
              {o}
            </span>
          ))}
        </div>
      ) : null}
    </Drawer>
  );
}

/**
 * Which project field a band's values come from.
 *
 * One control rather than a source picker beside a field picker: the source is
 * a property of the field, not a separate decision, and three groups in one
 * list is how it reads on screen. Fields with nothing in them are left out —
 * binding to one produces a dropdown with no options, which looks broken.
 */
function FieldPicker({
  value,
  onPick,
}: {
  value: FieldBinding | undefined;
  onPick: (field: FieldBinding | null) => void;
}): JSX.Element {
  const data = store.data();
  const key = value ? `${value.source}\u0000${value.name}` : '';

  const options: SearchOption[] = [{ value: '', label: 'A list I type' }];
  for (const source of SOURCE_NAMES) {
    for (const f of data.fields(source)) {
      if (store.fieldValues({ source, name: f }).length === 0) continue;
      options.push({ value: `${source}\u0000${f}`, label: prettyField(f), group: SOURCE_LABELS[source] });
    }
  }

  return (
    <SearchSelect
      value={key}
      title="Every cell in the band offers this field's values, and follows the project as they change."
      options={options}
      onChange={(raw) => {
        if (raw === '') return onPick(null);
        const [source, name] = raw.split('\u0000');
        onPick({ source: source as FieldBinding['source'], name: name! });
      }}
    />
  );
}

const SOURCE_LABELS: Record<FieldBinding['source'], string> = {
  bakedSections: 'Baked sections',
  rawSections: 'Raw sections',
  usages: 'Usages',
  areas: 'Areas',
};
